import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/primitives/tabs";
import { cn } from "@/shared/lib/utils";

export type ColocaTabItem = {
  /** Unique identifier — matched to the content panel rendered for this tab. */
  value: string;
  /** Text or element shown in the trigger button. */
  label: ReactNode;
  /**
   * Optional icon rendered before the label. Use to reinforce the tab's topic, e.g. `<SettingsIcon>` for a
   * settings tab or `<ShieldIcon>` for security.
   */
  icon?: LucideIcon;
  /** Content rendered when this tab is active. */
  content: ReactNode;
  /** Prevent selecting this tab when the feature is unavailable in the current context. */
  disabled?: boolean;
};

interface ColocaTabsProps {
  /** One entry per tab — each item renders one trigger button and one content panel. */
  tabs: ColocaTabItem[];
  /**
   * Initially selected tab value (uncontrolled). Defaults to the first tab when omitted. Use `value` +
   * `onValueChange` instead when you need to control the selection externally.
   */
  defaultValue?: string;
  /** Controlled selected tab value. Pair with `onValueChange`. */
  value?: string;
  /** Called when the user switches tabs. */
  onValueChange?: (value: string) => void;
  /**
   * Visual style of the tab list. `"default"` renders tabs as pill/chip buttons on a muted background.
   * `"line"` renders a minimal underline indicator — suitable for page-level navigation.
   */
  variant?: "default" | "line";
  /** `"horizontal"` stacks the tab list above the panels; `"vertical"` places it to the left. */
  orientation?: "horizontal" | "vertical";
  className?: string;
  /** Extra classes applied to the `TabsList` container — use for width, gap, or alignment. */
  listClassName?: string;
  /**
   * Extra classes applied to every `TabsTrigger`. Needed to reflow the row: a trigger is `flex-1` (basis 0)
   * and `whitespace-nowrap`, so without a `flex-basis` that forces a given number per line a wrapping list
   * overflows instead of wrapping. Pair with `flex-wrap` on `listClassName`.
   */
  triggerClassName?: string;
  /** Extra classes applied to every `TabsContent` panel. */
  contentClassName?: string;
}

/**
 * Use `ColocaTabs` when a view needs tabbed navigation between sections — pass a `tabs` array and
 * `ColocaTabs` handles the full `TabsList → TabsTrigger → TabsContent` composition automatically.
 *
 * For fully custom tab layouts — e.g. tabs with badges, lazy-loaded panels, or non-standard trigger content —
 * use the `Tabs` primitives directly.
 *
 * @summary tabbed navigation from a flat array — no manual composition needed
 */
export function ColocaTabs({
  tabs,
  defaultValue,
  value,
  onValueChange,
  variant = "default",
  orientation = "horizontal",
  className,
  listClassName,
  triggerClassName,
  contentClassName,
}: ColocaTabsProps) {
  return (
    <Tabs
      defaultValue={defaultValue ?? tabs[0]?.value}
      value={value}
      onValueChange={onValueChange as ((value: string | null) => void) | undefined}
      orientation={orientation}
      className={className}
    >
      <TabsList variant={variant} className={listClassName}>
        {tabs.map(({ value: v, label, icon: Icon, disabled }) => (
          <TabsTrigger key={v} value={v} disabled={disabled} className={triggerClassName}>
            {Icon && <Icon />}
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map(({ value: v, content }) => (
        <TabsContent key={v} value={v} className={cn(contentClassName)}>
          {content}
        </TabsContent>
      ))}
    </Tabs>
  );
}
